import { atom } from "jotai";
import { Node } from "@xyflow/react";
import { ImageNodeData } from "../types";
import { addNodeSetter } from "./addNodeSetter";
import { uploadImageToServerSetter } from "./uploadImageToServerSetter";
import { uuid } from "../utils/uuid";
import { calculateNodeDimensions } from "../utils/calculateNodeDimensions";
import { createNotificationSetter } from "../notifications";

type CreateImageNodeParams = {
  imageUrl: string; // Blob URL for immediate display
  position: { x: number; y: number };
  file: File;
  uploadMethod?: "drag_and_drop" | "copy_and_paste";
};

/**
 * Loads an image and returns its natural dimensions
 */
function getImageDimensions(
  imageUrl: string,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = () => {
      reject("Failed to load image into html image element");
    };
    img.src = imageUrl;
  });
}

/**
 * Creates an image node with blob URL for immediate display,
 * then triggers background upload to replace with server URL
 */
export const createImageNodeSetter = atom(
  null,
  async (
    _,
    set,
    { imageUrl, position, file, uploadMethod }: CreateImageNodeParams,
  ) => {
    const id = uuid();

    // Load image to get dimensions
    let nodeDimensions: { width: number; height: number };
    try {
      const imageDimensions = await getImageDimensions(imageUrl);
      nodeDimensions = calculateNodeDimensions(
        imageDimensions.width,
        imageDimensions.height,
      );
    } catch {
      set(createNotificationSetter, {
        type: "error",
        message:
          "Something went wrong while loading the image. Please try again, or upload a different file if the issue continues.",
      });
      return;
    }

    const newNode: Node<ImageNodeData> = {
      id,
      type: "image",
      position: {
        x: position.x - nodeDimensions.width / 2,
        y: position.y - nodeDimensions.height / 2,
      },
      data: {
        imgUrl: imageUrl,
      },
      width: nodeDimensions.width,
      height: nodeDimensions.height,
    };

    set(addNodeSetter, newNode);

    // Upload to server in background (non-blocking)
    // Image shows immediately, then swaps to server URL when ready
    set(uploadImageToServerSetter, {
      nodeId: id,
      file,
      uploadMethod,
      blobUrl: imageUrl,
    });
  },
);

createImageNodeSetter.debugLabel = "createImageNodeSetter";
